import React from 'react';
import { NO_TTY_JSON_OUTPUT_OPTION } from '@wix/cli-command-contract';
import type { CommandServices } from '@wix/cli-command-contract';
import { Option, createCommand } from '@commander-js/extra-typings';
import { createI18nT } from '../i18n.js';
import {
  validateBase,
  validateReleaseComment,
  validateSite,
} from './validations.js';
import { runCommand } from './render-command.js';

export const releaseCommand = (services: CommandServices) => {
  const t = createI18nT();

  return createCommand('release')
    .description(t('release_command_def.description'))
    .option(
      '-s, --site <site-id>',
      t('release_command_def.option.site'),
      validateSite
    )
    .option(
      '--base-url <url>',
      t('release_command_def.option.base_url'),
      validateBase
    )
    .option(
      '-c, --comment <comment>',
      t('release_command_def.option.comment'),
      validateReleaseComment
    )
    .addOption(
      new Option(
        '-t, --version-type <type>',
        t('release_command_def.option.version_type')
      ).choices(['major', 'minor'])
    )
    .addOption(
      new Option(
        NO_TTY_JSON_OUTPUT_OPTION,
        t('app_preview_command_def.option.json')
      ).hideHelp()
    )
    .action(async (options, command) => {
      const { renderCommand } = await import('./render-command.js');
      const { ReleaseCommand, ReleaseVersionType } = await import(
        '../components/ReleaseCommand/index.js'
      );

      const { baseUrl, site, comment, json } = options;

      const versionType = options.versionType
        ? {
            major: ReleaseVersionType.MAJOR,
            minor: ReleaseVersionType.MINOR,
          }[options.versionType]
        : undefined;

      const props = {
        baseUrl,
        siteIdOverride: site,
        preEnteredComment: comment,
        preSelectedVersionType: versionType,
      };

      if (json) {
        return runCommand(command, services, async (model) => {
          const { nonInteractiveRelease } = await import(
            '../non-interactive/release.js'
          );
          return nonInteractiveRelease(services, model, props);
        });
      }

      await renderCommand(command, services, <ReleaseCommand {...props} />);
    });
};
